import { CheckCircle2, Unlink } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EmptyState, SkeletonRows, errorMessage } from '@/shared/components/data'
import { Button, Field, Input } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { formatDateMedium, useI18n } from '@/shared/i18n'

import { useBriefPublico, useEnviarBrief } from './api'
import { CampoDeBrief } from './components/CampoDeBrief'
import type { Contacto, Respuesta } from './types'

const CONTACTO_VACIO: Contacto = { name: '', email: '', role: '', phone: '' }

/**
 * `/onboarding/:token` — **la única pantalla de la app sin sesión.**
 *
 * La abre alguien que todavía no tiene cuenta: el onboarding pasa justo antes.
 * Por eso no tiene ni barra lateral, ni espina de empresa, ni `⌘K`. Se nota de
 * un vistazo que no es ni la app interna ni el portal, que es lo mismo que hace
 * el portal para distinguirse de lo interno.
 *
 * Las preguntas NO están escritas acá: vienen del servidor, que es su única
 * fuente de verdad. Esta pantalla dibuja lo que recibe.
 */
export function OnboardingPage() {
  const { token = '' } = useParams()
  const t = useT()
  const { locale } = useI18n()
  const query = useBriefPublico(token)
  const enviar = useEnviarBrief(token)

  const [answers, setAnswers] = useState<Record<string, Respuesta>>({})
  const [contacto, setContacto] = useState<Contacto>(CONTACTO_VACIO)
  /**
   * Nada se marca en rojo hasta el primer intento de envío.
   *
   * Pintar de rojo los campos vacíos de un formulario de veinte preguntas
   * apenas se abre es decirle a alguien que hizo algo mal antes de que empiece.
   */
  const [intentado, setIntentado] = useState(false)

  const preguntas = useMemo(
    () => (query.data?.secciones ?? []).flatMap((s) => s.preguntas),
    [query.data],
  )

  const faltantes = useMemo(
    () => preguntas.filter((p) => p.requerida && !tieneContenido(answers[p.id])).map((p) => p.id),
    [preguntas, answers],
  )

  if (query.isPending) {
    return (
      <Marco>
        <SkeletonRows rows={8} withSpine={false} />
      </Marco>
    )
  }

  // Un link vencido, revocado o inexistente llega igual acá: el servidor no los
  // distingue a propósito, y esta pantalla tampoco puede.
  if (query.isError) {
    return (
      <Marco>
        <EmptyState
          icon={<Unlink size={18} aria-hidden="true" />}
          title={t('onboarding.public.invalidTitle')}
          description={t('onboarding.public.invalidDescription')}
        />
      </Marco>
    )
  }

  const brief = query.data

  if (brief.submitted) {
    return (
      <Marco>
        <div className="hairline flex flex-col items-center gap-2 rounded-[var(--radius-card)] bg-surface-1 px-6 py-10 text-center">
          <CheckCircle2 size={22} aria-hidden="true" style={{ color: 'hsl(var(--phase-done))' }} />
          <h2 className="font-display text-md text-text-1">{t('onboarding.public.doneTitle')}</h2>
          <p className="max-w-[52ch] text-xs text-text-2">
            {t('onboarding.public.doneDescription')}
          </p>
        </div>
      </Marco>
    )
  }

  const enviarBrief = () => {
    setIntentado(true)
    if (faltantes.length > 0 || !contacto.name.trim() || !contacto.email.trim()) return
    enviar.mutate({ answers, contacto })
  }

  return (
    <Marco>
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-xl text-text-1">{t('onboarding.public.title')}</h1>
        <p className="text-xs text-text-2">
          {t('onboarding.public.subtitle', {
            empresa: brief.company_name,
            proyecto: brief.project_name,
          })}
        </p>
        <p className="text-2xs text-text-3">
          {t('onboarding.public.expires', {
            fecha: formatDateMedium(brief.expires_at.slice(0, 10), locale),
          })}
        </p>
      </header>

      <p className="hairline rounded-[var(--radius-card)] bg-surface-1 px-3 py-2.5 text-xs text-text-2">
        {t('onboarding.public.instructions')}
      </p>

      <form
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault()
          enviarBrief()
        }}
      >
        {brief.secciones.map((seccion) => (
          <section key={seccion.id} className="flex flex-col gap-3">
            <h2 className="font-display text-base text-text-1">{seccion.titulo}</h2>
            {seccion.preguntas.map((pregunta) => (
              <CampoDeBrief
                key={pregunta.id}
                pregunta={pregunta}
                valor={answers[pregunta.id]}
                falta={intentado && faltantes.includes(pregunta.id)}
                onChange={(valor) => setAnswers((previo) => ({ ...previo, [pregunta.id]: valor }))}
              />
            ))}
          </section>
        ))}

        {/* ------------------------------------------------- tus datos */}
        <section className="flex flex-col gap-3">
          <h2 className="font-display text-base text-text-1">
            {t('onboarding.public.contactTitle')}
          </h2>
          <p className="text-2xs text-text-3">{t('onboarding.public.contactHint')}</p>

          <Field label={`${t('common.label.name')} *`} htmlFor="c-name">
            <Input
              id="c-name"
              value={contacto.name}
              style={
                intentado && !contacto.name.trim()
                  ? { borderColor: 'hsl(var(--overdue))' }
                  : undefined
              }
              onChange={(e) => setContacto((p) => ({ ...p, name: e.target.value }))}
            />
          </Field>
          <Field label={`${t('common.label.email')} *`} htmlFor="c-email">
            <Input
              id="c-email"
              type="email"
              value={contacto.email}
              style={
                intentado && !contacto.email.trim()
                  ? { borderColor: 'hsl(var(--overdue))' }
                  : undefined
              }
              onChange={(e) => setContacto((p) => ({ ...p, email: e.target.value }))}
            />
          </Field>
          <Field label={t('onboarding.public.contactRole')} htmlFor="c-role">
            <Input
              id="c-role"
              value={contacto.role}
              onChange={(e) => setContacto((p) => ({ ...p, role: e.target.value }))}
            />
          </Field>
          <Field label={t('onboarding.public.contactPhone')} htmlFor="c-phone">
            <Input
              id="c-phone"
              value={contacto.phone}
              onChange={(e) => setContacto((p) => ({ ...p, phone: e.target.value }))}
            />
          </Field>
        </section>

        <div className="flex flex-col gap-2 pb-10">
          {/*
            Cuántas faltan, y recién después del primer intento. Es más útil que
            un "completá los campos obligatorios": dice cuántos son y el navegador
            ya pintó cuáles.
          */}
          {intentado && faltantes.length > 0 ? (
            <span role="alert" className="text-xs" style={{ color: 'hsl(var(--overdue))' }}>
              {t('onboarding.public.missing', { count: faltantes.length })}
            </span>
          ) : null}
          {enviar.isError ? (
            <span role="alert" className="text-xs" style={{ color: 'hsl(var(--overdue))' }}>
              {errorMessage(enviar.error, t('onboarding.public.sendError'))}
            </span>
          ) : null}

          <Button
            variant="primary"
            type="submit"
            disabled={enviar.isPending}
            className="self-start"
          >
            {enviar.isPending ? t('onboarding.public.sending') : t('onboarding.public.send')}
          </Button>
          <span className="text-2xs text-text-3">{t('onboarding.public.oneShot')}</span>
        </div>
      </form>
    </Marco>
  )
}

/** El marco de la pantalla pública: centrado, angosto, sin nada de la app. */
function Marco({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-bg">
      <div className="mx-auto flex max-w-[820px] flex-col gap-6 px-4 py-8">{children}</div>
    </div>
  )
}

/** Misma regla que el servidor: una tabla con todo en blanco no está respondida. */
function tieneContenido(valor: Respuesta | undefined): boolean {
  if (valor === undefined) return false
  if (typeof valor === 'string') return valor.trim().length > 0
  return valor.some((fila) => fila.some((celda) => celda.trim().length > 0))
}
